import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthProvider";
import { fetchSubscriptionStatus, NONE } from "../Services/subscription";

/**
 * One subscription state for the whole app.
 *
 * Four components each fetched this separately, once on mount. Starting a trial
 * in one of them left the other three believing nothing had changed - and the
 * header, which never remounts, went on offering a free trial that was already
 * running. Sharing the state means starting a trial anywhere updates
 * everywhere.
 */
const SubscriptionContext = createContext({
  subscription: { state: NONE, daysRemaining: 0 },
  refreshSubscription: async () => {},
  loaded: false,
});

export const useSubscription = () => useContext(SubscriptionContext);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState({
    state: NONE,
    daysRemaining: 0,
  });

  // Distinguishes "no subscription" from "not asked yet". Without it every
  // screen renders its not-subscribed message for a moment on load, which reads
  // as the app telling a paying customer they have nothing.
  const [loaded, setLoaded] = useState(false);

  const refreshSubscription = useCallback(async () => {
    if (!user) {
      setSubscription({ state: NONE, daysRemaining: 0 });
      setLoaded(true);
      return;
    }

    const status = await fetchSubscriptionStatus();
    setSubscription(status);
    setLoaded(true);
    return status;
  }, [user]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const value = useMemo(
    () => ({ subscription, refreshSubscription, loaded }),
    [subscription, refreshSubscription, loaded]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
